// src/DashboardWidgets.js - VERSIONE CON REPORT INTERATTIVI

import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { useUser } from './UserContext';
import { collection, query, where, orderBy, limit, getDocs, doc, setDoc, onSnapshot, Timestamp, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/it';
import { FaBullhorn, FaSearch, FaChartLine, FaBed, FaUsers } from 'react-icons/fa';
import ClockInOutCard from './ClockInOutCard';

// --- UTILS E GRAFICI (Semplificati per Widget) ---

const LoadingWidget = () => <div style={{padding: '20px', textAlign: 'center', color: '#888'}}>Caricamento dati...</div>;

const SimpleLineChart = ({ data, dataKeys, colors, height = 150 }) => {
    if (!data || data.length === 0) return <div style={{textAlign:'center', color:'#ccc', padding:'20px'}}>Dati insufficienti</div>;
    const padding = 20;
    const width = 600; 
    const maxVal = Math.max(...data.map(d => Math.max(...dataKeys.map(k => d[k] || 0)))) * 1.1 || 100;
    const getX = (i) => padding + (i * (width - 2 * padding) / (data.length - 1));
    const getY = (v) => height - padding - ((v / maxVal) * (height - 2 * padding));
    return (
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
            {[0, 0.5, 1].map(pct => {
                const y = height - padding - (pct * (height - 2 * padding));
                return <line key={pct} x1={padding} y1={y} x2={width - padding} y2={y} stroke="#eee" />;
            })}
            {dataKeys.map((key, kIndex) => {
                const points = data.map((d, i) => `${getX(i)},${getY(d[key] || 0)}`).join(' ');
                return <polyline key={key} points={points} fill="none" stroke={colors[kIndex]} strokeWidth="3" />;
            })}
             {data.map((d, i) => (
                <text key={i} x={getX(i)} y={height - 5} textAnchor="middle" fontSize="10" fill="#666">{d.label}</text>
            ))}
        </svg>
    );
};

const SimpleBarChart = ({ data, dataKey, color, labelKey, height = 150 }) => {
    if (!data || data.length === 0) return <div style={{textAlign:'center', color:'#ccc', padding:'20px'}}>Dati insufficienti</div>;
    const padding = 20;
    const width = 600;
    const maxVal = Math.max(...data.map(d => d[dataKey] || 0)) * 1.1 || 10;
    const barWidth = (width - 2 * padding) / data.length * 0.5;
    return (
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
             {[0, 0.5, 1].map(pct => {
                 const y = height - padding - (pct * (height - 2 * padding));
                 return <line key={pct} x1={padding} y1={y} x2={width - padding} y2={y} stroke="#eee" strokeDasharray="4" />;
            })}
            {data.map((d, i) => {
                const x = padding + (i * (width - 2 * padding) / data.length) + ((width - 2 * padding) / data.length - barWidth)/2;
                const h = (d[dataKey] / maxVal) * (height - 2 * padding);
                return (
                    <g key={i}>
                        <rect x={x} y={height - padding - h} width={barWidth} height={h} fill={color} rx="2" />
                        <text x={x + barWidth/2} y={height - padding - h - 5} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#333">{d[dataKey]}</text>
                        <text x={x + barWidth/2} y={height - 5} textAnchor="middle" fontSize="9" fill="#555">{d[labelKey].substring(0, 6)}</text>
                    </g>
                );
            })}
        </svg>
    );
};

const WidgetDetailModal = ({ title, content, date, onClose, type }) => {
    if (!title) return null;

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0, 
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 9999, // Altissimo per stare sopra a tutto
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backdropFilter: 'blur(3px)'
            }}
            onClick={onClose}
        >
            <div 
                onClick={(e) => e.stopPropagation()} // Evita chiusura se clicchi dentro
                style={{
                    backgroundColor: 'white',
                    width: '90%',
                    maxWidth: '600px',
                    maxHeight: '80vh',
                    borderRadius: '8px',
                    padding: '25px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    overflowY: 'auto',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px'
                }}
            >
                <button 
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#666'
                    }}
                >
                    &times;
                </button>

                <div style={{borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
                    <h2 style={{margin: 0, color: type === 'doc' ? '#198754' : '#0d6efd'}}>
                        {type === 'doc' ? '📄 ' : '📢 '}{title}
                    </h2>
                    {date && <small style={{color: '#999'}}>{moment(date).format('DD MMMM YYYY, HH:mm')}</small>}
                </div>

                <div style={{lineHeight: '1.6', color: '#333'}}>
    {content ? (
        <div dangerouslySetInnerHTML={{ __html: content }} />
    ) : (
        "Nessun contenuto disponibile."
    )}
</div>

                <div style={{textAlign: 'right', marginTop: '10px'}}>
                    <button 
                        onClick={onClose} 
                        className="modal-button" // Usa le tue classi CSS esistenti se presenti, o stile inline
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// 1. WIDGET BASE
// =============================================================================

// --- PROSSIMO TURNO ---
export const NextShiftWidget = () => {
    const { userProfile } = useUser();
    const navigate = useNavigate();
    const [nextShift, setNextShift] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if(!userProfile?.centerId || !userProfile?.uid) return;

        const fetchShiftAndProfile = async () => {
            try {
                const now = moment();
                
                // 1. SCARICA IL PROFILO UTENTE AGGIORNATO (Per avere le fasce orarie fresche)
                const userDocRef = doc(db, 'centers', userProfile.centerId, 'staff', userProfile.uid);
                const userSnap = await getDoc(userDocRef);
                const latestTimeSlots = userSnap.exists() ? (userSnap.data().timeSlots || []) : [];

                // 2. Cerca nei turni (Mese corrente e successivo)
                const monthsToCheck = [now.format('YYYY-MM'), now.clone().add(1, 'month').format('YYYY-MM')];
                
                for (const m of monthsToCheck) {
                    const snap = await getDocs(query(collection(db, 'centers', userProfile.centerId, 'schedules'), where('__name__', '==', m)));
                    
                    if(!snap.empty) {
                        const scheduleData = JSON.parse(snap.docs[0].data().schedule || '{}');
                        const sortedDays = Object.keys(scheduleData).sort();

                        for (const day of sortedDays) {
                            // Salta giorni passati (ieri)
                            if (moment(day).isBefore(now, 'day')) continue;

                            const shift = scheduleData[day]?.[userProfile.uid] || scheduleData[day]?.[userProfile.name];

                            if (shift && !['Ferie', 'Malattia', 'CHIUSO', 'Permesso'].includes(shift.name)) {
                                
                                let timeString = "";
                                let shiftNameDisplay = shift.name === 'standard' ? 'Ufficio' : shift.name;
                                let endTimeMoment = null;
                                let timeSlotDetails = null;

                                // LOGICA DI RECUPERO FASCIA ORARIA (Esatta replica di Dashboard.js)
                                if (shift.name === 'Nuovo Orario' && shift.customTime) {
    timeSlotDetails = {
        startTime: shift.customTime.start,
        endTime: shift.customTime.end,
        isRecovery: shift.isRecovery
    };
} else if (latestTimeSlots.length > 0) {
    const shiftNameInSchedule = shift.originalName || shift.name;

    // 🔥 FUNZIONE HELPER PER TROVARE LO SLOT VALIDO NELLA DATA SPECIFICA 🔥
    const findValidSlot = (nameToSearch) => {
        return latestTimeSlots.find(ts => {
            const from = ts.validFrom || "2020-01-01";
            const until = ts.validUntil || "2100-01-01";
            // 'day' è la data del turno che stiamo processando nel loop
            return ts.name === nameToSearch && 
                   moment(day).isBetween(from, until, 'day', '[]');
        });
    };

    // A. Cerca la fascia base (es. "Mattina") valida per la data corrente
    timeSlotDetails = findValidSlot(shiftNameInSchedule);

    // B. Se il turno è marcato come Recupero, cerca variante specifica valida
    if (shift.isRecovery === true && timeSlotDetails) {
        const recoverySlot = findValidSlot(`${timeSlotDetails.name} REC`);
        if (recoverySlot) {
            timeSlotDetails = recoverySlot; 
        }
    }
    
    // C. Fallback: Cerca per nome esatto se non trovato prima
    if (!timeSlotDetails) {
        timeSlotDetails = findValidSlot(shift.name);
    }
}

                                if (timeSlotDetails) {
                                    timeString = `${timeSlotDetails.startTime} - ${timeSlotDetails.endTime}`;
                                    
                                    // Calcolo Momento Fine per verifica scadenza
                                    endTimeMoment = moment(`${day} ${timeSlotDetails.endTime}`, 'YYYY-MM-DD HH:mm');
                                    const startMoment = moment(`${day} ${timeSlotDetails.startTime}`, 'YYYY-MM-DD HH:mm');
                                    
                                    // Gestione turno notturno (fine il giorno dopo)
                                    if (startMoment.isAfter(endTimeMoment)) {
                                        endTimeMoment.add(1, 'day');
                                    }

                                    // CONTROLLO ORARIO SCADUTO
                                    // Se è oggi E l'orario di fine è già passato, il turno è finito. Cerca il prossimo.
                                    if (endTimeMoment.isBefore(now)) {
                                        continue; // Salta al prossimo ciclo (domani o prossimo turno)
                                    }
                                } else {
                                    // Se non trovo la fascia, non posso sapere se è scaduto con precisione.
                                    // Se è oggi, assumo che sia valido per mostrarlo (meglio mostrare info parziali che nulla)
                                    timeString = "Orario non disp.";
                                }

                                setNextShift({ 
                                    date: moment(day), 
                                    name: shiftNameDisplay, 
                                    time: timeString, 
                                    isRecovery: shift.isRecovery || (timeSlotDetails && timeSlotDetails.isRecovery)
                                });
                                setLoading(false);
                                return; // Trovato!
                            }
                        }
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error("Errore Widget:", error);
                setLoading(false);
            }
        };

        fetchShiftAndProfile();
    }, [userProfile]);

    if(loading) return <LoadingWidget />;

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/turni')}>
            <h3>🗓️ Il Tuo Prossimo Turno</h3>
            {nextShift ? (
                <>
                    <p className="highlight-text" style={{textTransform: 'capitalize'}}>
                        {nextShift.date.calendar(null, {
                            sameDay: '[Oggi] dddd D MMMM',
                            nextDay: '[Domani] dddd D MMMM',
                            nextWeek: 'dddd D MMMM',
                            sameElse: 'dddd D MMMM'
                        })}
                    </p>
                    <div className="shift-details-dashboard">
                         <span style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#333'}}>
                            {nextShift.name}
                         </span>
                         {nextShift.time && <span className="shift-time" style={{marginLeft: '10px'}}>{nextShift.time}</span>}
                         {nextShift.isRecovery && <span className="recovery-badge" style={{marginLeft: '10px'}}>Recupero</span>}
                    </div>
                </>
            ) : <p>Nessun turno programmato a breve.</p>}
        </div>
    );
};

// --- PROSSIMO EVENTO ---
export const NextEventWidget = () => {
    const { userProfile } = useUser();
    const navigate = useNavigate();
    const [nextEvent, setNextEvent] = useState(null);

    useEffect(() => {
        if(!userProfile?.centerId) return;
        const q = query(
            collection(db, 'centers', userProfile.centerId, 'calendarEvents'),
            where('start', '>=', new Date()),
            orderBy('start', 'asc'),
            limit(1)
        );
        const unsub = onSnapshot(q, (snap) => {
            const events = snap.docs.map(d => ({...d.data(), id: d.id, start: d.data().start.toDate(), end: d.data().end.toDate()}))
                .filter(e => e.type === 'shared' || e.ownerId === userProfile.uid || (e.sharedWith || []).includes(userProfile.uid));
            setNextEvent(events[0] || null);
        });
        return () => unsub();
    }, [userProfile]);

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/calendario')}>
            <h3>🗓️ Prossimo Evento</h3>
            {nextEvent ? (
                <>
                    <p className="highlight-text" style={{color: '#d0021b'}}>{moment(nextEvent.start).format('dddd D MMMM')}</p>
                    <div style={{fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '5px'}}>{nextEvent.title}</div>
                    <div className="shift-details-dashboard">
                        <span className="shift-time">
                            {nextEvent.allDay ? 'Tutto il giorno' : `${moment(nextEvent.start).format('HH:mm')} - ${moment(nextEvent.end).format('HH:mm')}`}
                        </span>
                        <span className={`tag-pill ${nextEvent.type === 'personal' ? 'personal-event-tag' : 'shared-event-tag'}`}>
                            {nextEvent.type === 'personal' ? 'Personale' : 'Globale'}
                        </span>
                    </div>
                </>
            ) : <p>Nessun evento futuro.</p>}
        </div>
    );
};

// --- DOCUMENTI RAPIDI ---
export const QuickDocsWidget = () => {
    const { userProfile } = useUser();
    const navigate = useNavigate(); // Manteniamo navigate per "Vedi tutti" se servisse in futuro
    const [docs, setDocs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredDocs, setFilteredDocs] = useState([]);
    
    // Stato per il modale
    const [selectedDoc, setSelectedDoc] = useState(null);

    useEffect(() => {
        if(!userProfile?.centerId) return;
        const q = query(collection(db, 'centers', userProfile.centerId, 'procedures'));
        getDocs(q).then(snap => {
            const allDocs = snap.docs.map(d => ({id: d.id, ...d.data()}));
            allDocs.sort((a, b) => a.title.localeCompare(b.title));
            setDocs(allDocs);
            setFilteredDocs(allDocs.slice(0, 5)); 
        });
    }, [userProfile]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredDocs(docs.slice(0, 5));
        } else {
            const lower = searchTerm.toLowerCase();
            const filtered = docs.filter(d => d.title.toLowerCase().includes(lower));
            setFilteredDocs(filtered.slice(0, 5));
        }
    }, [searchTerm, docs]);

    return (
        <>
            <div className="dashboard-card"> 
                <h3>📚 Documenti Rapidi</h3>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', border: '1px solid #ddd', borderRadius: '5px', padding: '5px 10px'}}>
                    <FaSearch color="#999"/>
                    <input 
                        type="text" 
                        placeholder="Cerca procedura..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{border: 'none', outline: 'none', width: '100%', fontSize: '0.9rem'}}
                    />
                </div>
                {filteredDocs.length > 0 ? (
                    <ul className="procedure-quick-list">
                        {filteredDocs.map(proc => (
                            <li 
                                key={proc.id} 
                                // MODIFICA: Apre il modale invece di navigare
                                onClick={() => setSelectedDoc(proc)}
                                style={{cursor: 'pointer'}} // Assicura che sembri cliccabile
                            >
                                <span>{proc.title}</span>
                                <span className="procedure-tag" style={{fontSize: '0.7rem'}}>Leggi</span> 
                            </li>
                        ))}
                    </ul>
                ) : <p style={{ color: '#888', textAlign: 'center' }}>Nessun documento trovato.</p>}
            </div>

            {/* MODALE DI SOLA VISUALIZZAZIONE */}
            {selectedDoc && (
                <WidgetDetailModal 
                    title={selectedDoc.title}
                    // Qui assumo che il contenuto sia in 'content' o 'description'. 
                    // Se hai un link al PDF, puoi adattare il modale per mostrare un link.
                    content={selectedDoc.content || selectedDoc.description || "Contenuto del documento non visualizzabile in anteprima."}
                    date={selectedDoc.updatedAt?.toDate() || selectedDoc.createdAt?.toDate()}
                    type="doc"
                    onClose={() => setSelectedDoc(null)}
                />
            )}
        </>
    );
};

// --- NOTE PERSONALI ---
export const PersonalNotesWidget = () => {
    const { userProfile } = useUser();
    const [note, setNote] = useState('');

    useEffect(() => {
        if(!userProfile?.centerId) return;
        const fetchNote = async () => {
            const q = query(collection(db, 'centers', userProfile.centerId, 'notes'), where('docId', '==', userProfile.uid));
            const snap = await getDocs(q);
            if(!snap.empty) setNote(snap.docs[0].data().content);
        };
        fetchNote();
    }, [userProfile]);

    const handleSave = async () => {
        const docRef = doc(db, 'centers', userProfile.centerId, 'notes', userProfile.uid);
        await setDoc(docRef, { content: note, docId: userProfile.uid }, { merge: true });
    };

    return (
        <div className="dashboard-card notes-card">
            <h3>🗒️ Note Personali</h3>
            <textarea 
                value={note} 
                onChange={(e) => setNote(e.target.value)} 
                onBlur={handleSave} 
                placeholder="Aggiungi note personali..." 
                style={{width: '100%', minHeight: '100px', border: '1px solid #ddd', borderRadius: '5px', padding: '10px', fontFamily: 'inherit'}}
            />
        </div>
    );
};

// --- PLANNING STANZE (ODIERNO) ---
export const RoomPlanningWidget = () => {
    const { userProfile } = useUser();
    const [displayAssignments, setDisplayAssignments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!userProfile?.centerId) return;
            const today = moment();
            const centerId = userProfile.centerId;
            
            // 1. Fetch parallelo di tutti i dati necessari
            const [assignmentsSnap, roomsSnap, doctorsSnap, specialtiesSnap, equipmentSnap] = await Promise.all([
                getDocs(query(collection(db, 'centers', centerId, "roomAssignments"), where("validUntil", ">=", today.format('YYYY-MM-DD')))),
                getDocs(collection(db, 'centers', centerId, "rooms")),
                getDocs(collection(db, 'centers', centerId, "doctors")),
                getDocs(collection(db, 'centers', centerId, "specialties")),
                getDocs(collection(db, 'centers', centerId, "equipment"))
            ]);

            // 2. Mappatura dati in oggetti facili da consultare
            const roomsList = roomsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const doctorsList = doctorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const specialtiesList = specialtiesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const equipmentList = equipmentSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            const allAssignments = assignmentsSnap.docs.map(d => d.data());
            const todayStr = today.format('YYYY-MM-DD');
            const dayOfWeek = today.isoWeekday();

            // 3. Filtro per oggi
            const todayAssignments = allAssignments.filter(a => {
                if (!(a.daysOfWeek || []).includes(dayOfWeek)) return false;
                if ((a.excludedDates || []).includes(todayStr)) return false;
                const isValid = today.isBetween(a.validFrom, a.validUntil, 'day', '[]');
                if (!isValid) return false;
                if (a.isRecurring) {
                    const start = moment(a.validFrom).startOf('isoWeek');
                    const diff = today.diff(start, 'weeks');
                    if (diff > 0 && diff % a.recurrenceInterval !== 0) return false;
                }
                return true;
            });

            // 4. Arricchimento Dati (Qui recuperiamo i Macchinari dalla STANZA)
            const enriched = todayAssignments.map(data => {
                const room = roomsList.find(r => r.id === data.roomId);
                const doctor = doctorsList.find(d => d.id === data.doctorId);
                
                // Specialistiche dal Dottore
                const specialtyNames = doctor?.specialtyIds?.map(id => 
                    specialtiesList.find(s => s.id === id)?.name
                ).filter(Boolean) || [];
                
                // Macchinari dalla Stanza
                const equipmentNames = room?.equipmentIds?.map(id => 
                    equipmentList.find(e => e.id === id)?.name
                ).filter(Boolean) || [];

                return { 
                    ...data, 
                    roomName: room?.name || 'N/D', 
                    doctorName: doctor ? `${doctor.firstName} ${doctor.lastName}` : 'N/D', 
                    specialtyNames, 
                    equipmentNames 
                };
            }).sort((a, b) => a.startTime.localeCompare(b.startTime));

            setDisplayAssignments(enriched);
            setLoading(false);
        };
        fetchData();
    }, [userProfile]);

    if (loading) return <LoadingWidget />;

    return (
        <div className="dashboard-card"> {/* La classe full-width è gestita dal wrapper in Dashboard.js */}
             <h3>🏨 Planning Stanze di Oggi ({moment().format('DD/MM')})</h3>
             <div className="assignments-list">
                {displayAssignments.length > 0 ? displayAssignments.map((a, i) => (
                    <div key={i} className="assignment-item">
                        <div className="assignment-item-header">
                            <span className="room">{a.roomName}</span>
                            <span className="time">{a.startTime} - {a.endTime}</span>
                        </div>
                        <div className="assignment-doctor-wrapper">
                            <span className="doctor">{a.doctorName}</span>
                        </div>
                        
                        {/* --- BLOCCO TAG SEPARATI --- */}
                        <div className="tag-container-dashboard">
                             {/* RIGA 1: Specialistiche */}
                             {a.specialtyNames?.length > 0 && (
                                <div className="tag-group">
                                    <span className="tag-label">Specialistiche:</span>
                                    {a.specialtyNames.map(name => (
                                        <span key={name} className="tag-pill tag-specialty">{name}</span>
                                    ))}
                                </div>
                             )}
                             
                             {/* RIGA 2: Macchinari (Se presenti) */}
                             {a.equipmentNames?.length > 0 && (
                                <div className="tag-group">
                                    <span className="tag-label">Macchinari:</span>
                                    {a.equipmentNames.map(name => (
                                        <span key={name} className="tag-pill tag-equipment">{name}</span>
                                    ))}
                                </div>
                             )}
                        </div>
                        {/* --- FINE BLOCCO TAG --- */}

                    </div>
                )) : <p style={{textAlign: 'center', padding: '20px', color: '#666'}}>Nessuna assegnazione stanze per oggi.</p>}
             </div>
        </div>
    );
};

// --- BACHECA ---
export const AnnouncementsWidget = () => {
    const { userProfile } = useUser();
    const navigate = useNavigate();
    const [announcements, setAnnouncements] = useState([]);
    
    // Stati per ricerca e modale
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

    useEffect(() => {
        if(!userProfile?.centerId) return;
        // Aumentato limit a 20 per rendere la ricerca utile nel widget
        const q = query(collection(db, 'centers', userProfile.centerId, 'announcements'), orderBy('createdAt', 'desc'), limit(20));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setAnnouncements(data);
        });
        return () => unsub();
    }, [userProfile]);

    // Filtra in base alla ricerca
    const filteredAnnouncements = useMemo(() => {
        if (!searchTerm.trim()) return announcements.slice(0, 3); // Default mostra solo i primi 3
        return announcements.filter(a => 
            a.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
            a.content.toLowerCase().includes(searchTerm.toLowerCase())
        ).slice(0, 5); // Se cerco, mostro fino a 5 risultati
    }, [announcements, searchTerm]);

    return (
        <>
            <div className="dashboard-card" style={{borderLeft: '5px solid #0d6efd'}}>
                <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px'}}>
                    <FaBullhorn style={{color: '#0d6efd'}} /> Bacheca Aziendale
                </h3>

                {/* CAMPO RICERCA INTELLIGENTE */}
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', border: '1px solid #ddd', borderRadius: '5px', padding: '5px 10px', backgroundColor: '#f9f9f9'}}>
                    <FaSearch color="#999"/>
                    <input 
                        type="text" 
                        placeholder="Cerca negli annunci..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{border: 'none', outline: 'none', width: '100%', fontSize: '0.9rem', background: 'transparent'}}
                    />
                </div>

                {filteredAnnouncements.length > 0 ? (
                    <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                        {filteredAnnouncements.map((ann) => (
                            <li 
                                key={ann.id} 
                                // Cliccando apro il modale invece di navigare
                                onClick={() => setSelectedAnnouncement(ann)}
                                style={{
                                    marginBottom: '10px', 
                                    paddingBottom: '10px', 
                                    borderBottom: '1px solid #eee', 
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                    padding: '5px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <div style={{fontWeight: 'bold', color: '#333', fontSize: '0.95rem'}}>
                                    {ann.title}
                                </div>
                                <div style={{fontSize: '0.85rem', color: '#666', marginTop: '2px'}}>
                                    {ann.content.substring(0, 60)}...
                                </div>
                                <div style={{fontSize: '0.75rem', color: '#999', marginTop: '2px'}}>
                                    {ann.createdAt ? moment(ann.createdAt.toDate()).format('DD/MM/YYYY') : ''}
                                </div>
                            </li>
                        ))}
                        <div style={{textAlign: 'right', marginTop: '10px'}}>
                            <span onClick={() => navigate('/bacheca')} style={{color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold'}}>
                                Gestisci / Vedi Archivio →
                            </span>
                        </div>
                    </ul>
                ) : (
                    <p style={{color: '#888', textAlign: 'center', padding: '10px'}}>
                        {searchTerm ? "Nessun annuncio trovato." : "Nessun avviso recente."}
                    </p>
                )}
            </div>

            {/* MODALE DI SOLA VISUALIZZAZIONE */}
            {selectedAnnouncement && (
                <WidgetDetailModal 
                    title={selectedAnnouncement.title}
                    content={selectedAnnouncement.content}
                    date={selectedAnnouncement.createdAt?.toDate()}
                    type="announcement"
                    onClose={() => setSelectedAnnouncement(null)}
                />
            )}
        </>
    );
};

// --- WIDGET REPORTISTICA COMPLETO (NUOVO) ---
export const ReportsWidget = () => {
    const { userProfile } = useUser();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('finanza'); // finanza, stanze, hr
    const [chartData, setChartData] = useState(null);
    const [kpis, setKpis] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userProfile?.centerId) return;
        
        const loadData = async () => {
            setLoading(true);
            const centerId = userProfile.centerId;
            const currentMonth = moment().format('YYYY-MM');
            const last6Months = [];
            for (let i = 5; i >= 0; i--) last6Months.push(moment().subtract(i, 'months').format('YYYY-MM'));

            try {
                // Fetch Summaries per ultimi 6 mesi
                const summQ = query(collection(db, 'centers', centerId, 'monthlySummaries'), where('monthYear', 'in', last6Months));
                const summSnap = await getDocs(summQ);
                const summaries = summSnap.docs.map(d => d.data());

                // Aggrega Dati Finanziari (Stima)
                const trendData = last6Months.map(m => {
                    const monthSumms = summaries.filter(s => s.monthYear === m);
                    const cost = monthSumms.reduce((acc, s) => acc + (s.realCost || 0), 0);
                    return { label: moment(m, 'YYYY-MM').format('MMM'), value: cost };
                });

                // Aggrega KPI HR (Assenze questo mese)
                const currentMonthSumms = summaries.filter(s => s.monthYear === currentMonth);
                const totalSick = currentMonthSumms.reduce((acc, s) => acc + (s.sicknessDays || 0), 0);
                const totalVacation = currentMonthSumms.reduce((acc, s) => acc + (s.leaveDays?.Ferie || 0), 0);

                // Fetch Occupazione Stanze (Ultimi 30gg) - semplificato
                // In una implementazione reale, questo richiederebbe una Cloud Function aggregata per performance
                // Qui simuliamo un dato statico o minimo per evitare query pesanti sul client in un widget
                const roomUsageData = [
                    { label: 'Stanza 1', value: 120 }, { label: 'Stanza 2', value: 90 }, { label: 'Stanza 3', value: 150 }
                ];

                setChartData({ trend: trendData, rooms: roomUsageData });
                setKpis({ totalSick, totalVacation });

            } catch (e) {
                console.error("Errore fetch reports widget", e);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [userProfile]);

    if (loading) return <LoadingWidget />;

    return (
        <div className="dashboard-card full-width">
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px'}}>
                <h3 style={{margin:0}}>📈 Reportistica Rapida</h3>
                <button onClick={() => navigate('/reports')} style={{background:'none', border:'none', color:'#0d6efd', cursor:'pointer', fontSize:'0.9rem'}}>Vedi dettagli →</button>
            </div>

            <div style={{display:'flex', gap:'10px', marginBottom:'15px', borderBottom:'1px solid #eee'}}>
                <button onClick={() => setActiveTab('finanza')} style={{padding:'8px 15px', background:'none', border:'none', borderBottom: activeTab==='finanza'?'2px solid #0d6efd':'none', color: activeTab==='finanza'?'#0d6efd':'#666', cursor:'pointer'}}>💰 Costi</button>
                <button onClick={() => setActiveTab('hr')} style={{padding:'8px 15px', background:'none', border:'none', borderBottom: activeTab==='hr'?'2px solid #0d6efd':'none', color: activeTab==='hr'?'#0d6efd':'#666', cursor:'pointer'}}>👥 HR</button>
                <button onClick={() => setActiveTab('stanze')} style={{padding:'8px 15px', background:'none', border:'none', borderBottom: activeTab==='stanze'?'2px solid #0d6efd':'none', color: activeTab==='stanze'?'#0d6efd':'#666', cursor:'pointer'}}>🏥 Stanze</button>
            </div>

            {activeTab === 'finanza' && (
                <div style={{height: '180px'}}>
                    <p style={{fontSize:'0.8rem', color:'#666', marginBottom:'5px'}}>Trend Costi Personale (Ultimi 6 Mesi)</p>
                    <SimpleLineChart data={chartData?.trend} dataKeys={['value']} colors={['#198754']} height={150} />
                </div>
            )}

            {activeTab === 'hr' && (
                <div style={{display:'flex', justifyContent:'space-around', paddingTop:'20px'}}>
                    <div style={{textAlign:'center'}}>
                        <div style={{fontSize:'2rem', fontWeight:'bold', color:'#dc3545'}}>{kpis.totalSick}</div>
                        <div style={{color:'#666', fontSize:'0.9rem'}}>Giorni Malattia (Mese)</div>
                    </div>
                    <div style={{textAlign:'center'}}>
                        <div style={{fontSize:'2rem', fontWeight:'bold', color:'#0d6efd'}}>{kpis.totalVacation}</div>
                        <div style={{color:'#666', fontSize:'0.9rem'}}>Giorni Ferie (Mese)</div>
                    </div>
                </div>
            )}

            {activeTab === 'stanze' && (
                <div style={{height: '180px'}}>
                     <p style={{fontSize:'0.8rem', color:'#666', marginBottom:'5px'}}>Top Occupazione (Stima)</p>
                     <SimpleBarChart data={chartData?.rooms} dataKey="value" labelKey="label" color="#0dcaf0" height={150} />
                </div>
            )}
        </div>
    );
};

// --- ALTRI WIDGET DI COLLEGAMENTO ---

export const LeavesAdminWidget = () => {
    const navigate = useNavigate();
    const { userProfile } = useUser();
    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        if (!userProfile?.centerId) return;
        
        // Query per contare le richieste in stato 'pending'
        const q = query(
            collection(db, 'centers', userProfile.centerId, 'leaveRequests'),
            where('status', '==', 'pending')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPendingCount(snapshot.size);
        });

        return () => unsubscribe();
    }, [userProfile]);

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/assenze')}>
            <h3>👥 Gestione Assenze</h3>
            <div style={{textAlign: 'center', padding: '15px 0'}}>
                 <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: pendingCount > 0 ? '#dc3545' : '#198754'}}>
                    {pendingCount}
                </div>
                <div style={{fontSize: '1rem', color: '#666'}}>Richieste da approvare</div>
            </div>
            <p style={{textAlign: 'center', color: '#888', fontSize: '0.8rem', marginTop: '0'}}>
                {pendingCount > 0 ? "Clicca per gestire" : "Nessuna richiesta in sospeso"}
            </p>
        </div>
    );
};
export const LeavesSelfWidget = () => {
    const navigate = useNavigate();
    const { userProfile } = useUser();

    // Leggiamo i dati direttamente dal profilo utente che è già caricato nel contesto
    const vacationBalance = userProfile?.leaveBalances?.vacationBalance || 0;
    const rolBalance = userProfile?.leaveBalances?.rolBalance || 0;

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/assenze')}>
            <h3>🏖️ Le Tue Assenze</h3>
            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-around' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0d6efd' }}>
                        {vacationBalance.toFixed(1)}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Giorni Ferie</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#198754' }}>
                        {rolBalance.toFixed(1)}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Ore ROL</div>
                </div>
            </div>
            <p style={{ marginTop: '15px', fontSize: '0.8rem', color: '#888', textAlign: 'center' }}>
                Clicca per richiedere un permesso
            </p>
        </div>
    );
};
export const TimeclockAdminWidget = () => {
    const navigate = useNavigate();
    const { userProfile } = useUser();
    const [activeCount, setActiveCount] = useState(0);

    useEffect(() => {
        if (!userProfile?.centerId) return;

        const todayStart = Timestamp.fromDate(moment().startOf('day').toDate());
        const todayEnd = Timestamp.fromDate(moment().endOf('day').toDate());

        // Ascolta i log di oggi per contare chi è dentro
        const q = query(
            collection(db, 'centers', userProfile.centerId, 'timelogs'),
            where('timestamp', '>=', todayStart),
            where('timestamp', '<=', todayEnd)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const logs = snapshot.docs.map(doc => doc.data());
            // Raggruppa per utente e trova l'ultimo stato
            const userStatusMap = {};
            
            // Ordina per tempo (dal più vecchio al più nuovo)
            logs.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis());

            logs.forEach(log => {
                // Sovrascrive con l'ultimo stato
                if (log.type === 'clock-in' || log.type === 'end-break') {
                    userStatusMap[log.userId] = 'active';
                } else {
                    userStatusMap[log.userId] = 'inactive';
                }
            });

            // Conta quanti sono 'active'
            const count = Object.values(userStatusMap).filter(status => status === 'active').length;
            setActiveCount(count);
        });

        return () => unsubscribe();
    }, [userProfile]);

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/timbrature')}>
            <h3>⏱️ Monitor Presenze</h3>
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: activeCount > 0 ? '#198754' : '#6c757d' }}>
                    {activeCount}
                </div>
                <div style={{ fontSize: '1rem', color: '#555' }}>
                    Dipendenti attualmente al lavoro
                </div>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#888', textAlign: 'center' }}>
                Clicca per vedere il dettaglio
            </p>
        </div>
    );
};
export const TimeclockSelfWidget = ClockInOutCard;

export const PayrollWidget = () => {
    const navigate = useNavigate();
    const { userProfile } = useUser();
    const [stats, setStats] = useState({ calculated: 0, totalStaff: 0 });
    
    useEffect(() => {
        if (!userProfile?.centerId) return;
        
        const lastMonth = moment().subtract(1, 'month').format('YYYY-MM');
        const centerId = userProfile.centerId;

        // Conta staff attivo (incluso nei turni)
        const qStaff = query(collection(db, 'centers', centerId, 'staff'), where('includeInShifts', '==', true));
        
        // Conta riepiloghi già calcolati per il mese scorso
        const qSummaries = query(
            collection(db, 'centers', centerId, 'monthlySummaries'),
            where('monthYear', '==', lastMonth)
        );

        // Listener paralleli
        const unsubStaff = onSnapshot(qStaff, snap => setStats(prev => ({...prev, totalStaff: snap.size})));
        const unsubSumm = onSnapshot(qSummaries, snap => setStats(prev => ({...prev, calculated: snap.size})));

        return () => { unsubStaff(); unsubSumm(); };
    }, [userProfile]);

    const percent = stats.totalStaff > 0 ? (stats.calculated / stats.totalStaff) * 100 : 0;
    const isComplete = stats.calculated === stats.totalStaff && stats.totalStaff > 0;

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/paghe')}>
            <h3>💰 Gestione Paghe</h3>
            <div style={{marginTop: '10px'}}>
                 <p style={{margin: '5px 0', fontWeight: 'bold', color: '#555', textTransform: 'capitalize'}}>
                    {moment().subtract(1, 'month').format('MMMM YYYY')}
                 </p>
                 <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px'}}>
                    <span style={{fontSize: '0.9rem', color: '#666'}}>Stato elaborazione:</span>
                    <span style={{fontSize: '1.2rem', fontWeight: 'bold', color: isComplete ? '#198754' : '#e67e22'}}>
                        {stats.calculated} / {stats.totalStaff}
                    </span>
                 </div>
                 {/* Barra di avanzamento */}
                 <div style={{height: '6px', marginTop: '8px', backgroundColor: '#eee', borderRadius: '3px', overflow: 'hidden'}}>
                    <div 
                        style={{
                            width: `${percent}%`,
                            height: '100%',
                            backgroundColor: isComplete ? '#198754' : '#e67e22',
                            transition: 'width 0.5s ease'
                        }}
                    />
                 </div>
            </div>
        </div>
    );
};

// 3. WIDGET: NOTE SPESA (Contatore Pendenti)
export const ExpenseNotesWidget = () => {
    const navigate = useNavigate();
    const { userProfile } = useUser();
    const [pendingAmount, setPendingAmount] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    
    const isManager = userProfile?.permissions?.expenseNotes === 'completo' || 
                      userProfile?.role === 'Proprietario' || 
                      userProfile?.role === 'Super Admin';

    useEffect(() => {
        if (!userProfile?.centerId) return;

        let q;
        if (isManager) {
            // Il Manager vede TUTTE le richieste in attesa
            q = query(
                collection(db, 'centers', userProfile.centerId, 'expenseNotes'),
                where('status', '==', 'pending')
            );
        } else {
            // L'utente vede le SUE richieste in attesa
            q = query(
                collection(db, 'centers', userProfile.centerId, 'expenseNotes'),
                where('userId', '==', userProfile.uid),
                where('status', '==', 'pending')
            );
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            let total = 0;
            snapshot.forEach(doc => { total += (doc.data().importo || 0); });
            setPendingCount(snapshot.size);
            setPendingAmount(total);
        });

        return () => unsubscribe();
    }, [userProfile, isManager]);

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/note-spesa')}>
            <h3>💸 Note Spesa</h3>
            <div style={{textAlign: 'center', padding: '10px 0'}}>
                {pendingCount > 0 ? (
                    <>
                        <div style={{fontSize: '1.8rem', fontWeight: 'bold', color: '#e67e22'}}>
                            € {pendingAmount.toFixed(2)}
                        </div>
                        <div style={{fontSize: '0.9rem', color: '#666'}}>
                            {pendingCount} richieste in attesa
                        </div>
                    </>
                ) : (
                    <div style={{color: '#198754', padding: '10px'}}>
                        <span style={{fontSize: '1.5rem'}}>✔</span> <br/>
                        Nessuna richiesta in sospeso
                    </div>
                )}
            </div>
        </div>
    );
};

// 4. WIDGET: BUSTE PAGA (Ultima Caricata / Caricamenti Admin)
export const PayslipsWidget = () => {
    const navigate = useNavigate();
    const { userProfile } = useUser();
    const [latestPayslip, setLatestPayslip] = useState(null); // Per utente
    const [uploadedCount, setUploadedCount] = useState(0); // Per admin

    const isManager = userProfile?.permissions?.payslips === 'completo' || 
                      userProfile?.role === 'Proprietario' || 
                      userProfile?.role === 'Super Admin';

    useEffect(() => {
        if (!userProfile?.centerId) return;
        
        if (isManager) {
            // Manager: conta quante buste paga sono state caricate per il mese scorso
            const lastMonth = moment().subtract(1, 'month').format('YYYY-MM');
            const q = query(
                collection(db, 'centers', userProfile.centerId, 'payslips'),
                where('monthYear', '==', lastMonth)
            );
            const unsub = onSnapshot(q, snap => setUploadedCount(snap.size));
            return () => unsub();
        } else {
            // Utente: trova la sua ultima busta paga
            const q = query(
                collection(db, 'centers', userProfile.centerId, 'payslips'),
                where('userId', '==', userProfile.uid),
                orderBy('monthYear', 'desc'),
                limit(1)
            );
            const unsub = onSnapshot(q, snap => {
                if (!snap.empty) setLatestPayslip(snap.docs[0].data());
            });
            return () => unsub();
        }
    }, [userProfile, isManager]);

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/buste-paga')}>
            <h3>📄 Buste Paga</h3>
            <div style={{marginTop: '10px'}}>
                {isManager ? (
                    // VISTA MANAGER
                    <div style={{textAlign: 'center'}}>
                         <p style={{margin: '5px 0', color: '#666', fontSize: '0.9rem'}}>
                             Caricate per <strong>{moment().subtract(1, 'month').format('MMMM')}</strong>
                         </p>
                         <div style={{fontSize: '2rem', color: '#0d6efd', fontWeight: 'bold', margin: '5px 0'}}>
                            {uploadedCount}
                         </div>
                         <p style={{fontSize: '0.8rem', color: '#888', margin: 0}}>Clicca per gestire</p>
                    </div>
                ) : (
                    // VISTA UTENTE
                    latestPayslip ? (
                        <div style={{textAlign: 'center', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee'}}>
                             <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '5px'}}>Ultima disponibile:</div>
                             <div style={{fontSize: '1.1rem', fontWeight: 'bold', color: '#333', textTransform: 'capitalize'}}>
                                 {moment(latestPayslip.monthYear, 'YYYY-MM').format('MMMM YYYY')}
                             </div>
                             <div style={{marginTop: '8px', color: '#198754', fontSize: '0.8rem', fontWeight: 'bold'}}>
                                 Scarica PDF
                             </div>
                        </div>
                    ) : (
                        <p style={{textAlign: 'center', color: '#999', padding: '20px', fontStyle: 'italic'}}>
                            Nessuna busta paga disponibile.
                        </p>
                    )
                )}
            </div>
        </div>
    );
};

// =============================================================================
// WIDGET DEDICATI AI MEDICI
// =============================================================================

export const DoctorCompensationsWidget = () => {
    const { userProfile } = useUser();
    const navigate = useNavigate();
    const [tallies, setTallies] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userProfile?.centerId || !userProfile?.isDoctorProfile) return;
        const fetchData = async () => {
            try {
                const centerId = userProfile.centerId;
                // Trova il VERO ID Medico
                const doctorQuery = query(collection(db, 'centers', centerId, 'doctors'), where('authUid', '==', userProfile.uid));
                const doctorSnap = await getDocs(doctorQuery);
                const realDoctorId = !doctorSnap.empty ? doctorSnap.docs[0].id : userProfile.uid;

                const q = query(collection(db, 'centers', centerId, 'doctorTallies'), where('doctorId', '==', realDoctorId));
                const snap = await getDocs(q);
                
                // Ordina per mese decrescente e prendi gli ultimi 3
                const data = snap.docs.map(d => ({id: d.id, ...d.data()}))
                    .sort((a, b) => b.monthId.localeCompare(a.monthId))
                    .slice(0, 3);
                
                setTallies(data);
            } catch (e) { console.error(e); } finally { setLoading(false); }
        };
        fetchData();
    }, [userProfile]);

    if (loading) return <LoadingWidget />;

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/miei-compensi')}>
            <h3>💰 Ultimi Compensi</h3>
            {tallies.length > 0 ? (
                <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px'}}>
                    {tallies.map(t => (
                        <div key={t.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '10px', border: `1px solid ${t.status === 'approved' ? '#bbf7d0' : t.status === 'rejected' ? '#fecaca' : '#e2e8f0'}`}}>
                            <div>
                                <div style={{fontWeight: '700', color: '#1e293b', textTransform: 'capitalize'}}>{moment(t.monthId, 'YYYY-MM').format('MMMM YYYY')}</div>
                                <div style={{fontSize: '0.75rem', fontWeight: 'bold', color: t.status === 'approved' ? '#16a34a' : t.status === 'rejected' ? '#dc2626' : '#d97706'}}>
                                    {t.status === 'approved' ? 'APPROVATO' : t.status === 'rejected' ? 'DA REVISIONARE' : 'IN ATTESA'}
                                </div>
                            </div>
                            <div style={{fontWeight: '900', fontSize: '1.2rem', color: '#0f172a'}}>
                                € {parseFloat(t.totalAmount || 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits:2})}
                            </div>
                        </div>
                    ))}
                </div>
            ) : <p style={{color: '#64748b', textAlign: 'center', padding: '10px'}}>Nessun compenso generato finora.</p>}
        </div>
    );
};

export const DoctorPlanningWidget = () => {
    const { userProfile } = useUser();
    const navigate = useNavigate();
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userProfile?.centerId || !userProfile?.isDoctorProfile) return;
        const fetchData = async () => {
            try {
                const centerId = userProfile.centerId;
                const doctorQuery = query(collection(db, 'centers', centerId, 'doctors'), where('authUid', '==', userProfile.uid));
                const doctorSnap = await getDocs(doctorQuery);
                const realDoctorId = !doctorSnap.empty ? doctorSnap.docs[0].id : userProfile.uid;

                const roomsSnap = await getDocs(collection(db, 'centers', centerId, 'rooms'));
                const roomsList = roomsSnap.docs.map(d => ({id: d.id, name: d.data().name}));

                const q = query(collection(db, 'centers', centerId, 'roomAssignments'), where('doctorId', '==', realDoctorId));
                const snap = await getDocs(q);
                const allAss = snap.docs.map(d => d.data());
                
                const weekAssignments = [];
                for (let i = 0; i < 7; i++) {
                    const currentDay = moment().startOf('isoWeek').add(i, 'days');
                    const dayStr = currentDay.format('YYYY-MM-DD');
                    const dayOfWeek = currentDay.isoWeekday();

                    allAss.forEach(a => {
                        if (!(a.daysOfWeek || []).includes(dayOfWeek)) return;
                        if ((a.excludedDates || []).includes(dayStr)) return;
                        if (!currentDay.isBetween(a.validFrom, a.validUntil, 'day', '[]')) return;
                        if (a.isRecurring) {
                            const start = moment(a.validFrom).startOf('isoWeek');
                            if (currentDay.diff(start, 'weeks') % (a.recurrenceInterval || 1) !== 0) return;
                        }
                        weekAssignments.push({
                            date: currentDay,
                            dateStr: currentDay.format('dddd DD/MM'),
                            startTime: a.startTime,
                            endTime: a.endTime,
                            roomName: roomsList.find(r => r.id === a.roomId)?.name || 'Stanza N/D'
                        });
                    });
                }
                
                weekAssignments.sort((a,b) => a.date.unix() - b.date.unix() || a.startTime.localeCompare(b.startTime));
                setAssignments(weekAssignments);
            } catch (e) { console.error(e); } finally { setLoading(false); }
        };
        fetchData();
    }, [userProfile]);

    if (loading) return <LoadingWidget />;

    return (
        <div className="dashboard-card clickable-card" onClick={() => navigate('/stanze')}>
            <h3>📅 Il Mio Planning (Questa Settimana)</h3>
            {assignments.length > 0 ? (
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '15px', maxHeight: '220px', overflowY: 'auto', paddingRight: '5px'}}>
                    {assignments.map((a, i) => {
                        const isToday = a.date.isSame(moment(), 'day');
                        return (
                            <div key={i} style={{display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderRadius: '8px', backgroundColor: isToday ? '#eff6ff' : '#f8fafc', border: isToday ? '1px solid #bfdbfe' : '1px solid #f1f5f9', alignItems: 'center'}}>
                                <div>
                                    <div style={{fontWeight: '700', textTransform: 'capitalize', color: isToday ? '#1d4ed8' : '#334155'}}>
                                        {isToday ? 'Oggi' : a.dateStr}
                                    </div>
                                    <div style={{fontSize: '0.8rem', color: '#64748b', marginTop: '2px'}}>{a.roomName}</div>
                                </div>
                                <div style={{fontWeight: '900', color: isToday ? '#1e40af' : '#475569'}}>{a.startTime} - {a.endTime}</div>
                            </div>
                        );
                    })}
                </div>
            ) : <p style={{color: '#64748b', textAlign: 'center', padding: '15px'}}>Nessun turno assegnato questa settimana.</p>}
        </div>
    );
};

export const DoctorStatsWidget = () => {
    const { userProfile } = useUser();
    const [stats, setStats] = useState({ prod: 0, comp: 0, appts: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userProfile?.centerId || !userProfile?.isDoctorProfile) return;
        const fetchData = async () => {
            try {
                const centerId = userProfile.centerId;
                const doctorQuery = query(collection(db, 'centers', centerId, 'doctors'), where('authUid', '==', userProfile.uid));
                const doctorSnap = await getDocs(doctorQuery);
                const realDoctorId = !doctorSnap.empty ? doctorSnap.docs[0].id : userProfile.uid;

                const startOfMonth = moment().startOf('month').toDate();
                const endOfMonth = moment().endOf('month').toDate();

                const qAppt = query(collection(db, 'centers', centerId, 'appointments'), where('start', '>=', startOfMonth), where('start', '<=', endOfMonth));
                const apptSnap = await getDocs(qAppt);

                let totalProd = 0, totalComp = 0, totalAppts = 0;

                apptSnap.docs.forEach(doc => {
                    const a = { id: doc.id, ...doc.data() };
                    const isDoctorInvolved = a.doctorId === realDoctorId || (a.services && a.services.some(s => s.doctorId === realDoctorId));
                    if (!isDoctorInvolved) return;
                    
                    // Solo appuntamenti confermati/pagati
                    if (a.status === 'completed' || a.status === 'accepted' || a.paid) {
                        totalAppts++;
                        
                        // 🎯 FIX CHIRURGICO: Replica la logica esatta di ReportsPage.js
                        const services = a.services && a.services.length > 0 ? a.services : [{ 
                            id: a.serviceId, serviceId: a.serviceId, price: a.price || 0, doctorId: a.doctorId 
                        }];

                        services.forEach(srv => {
                            if (srv.doctorId !== realDoctorId && a.doctorId !== realDoctorId) return;
                            
                            // 1. Calcolo Base Reale
                            let basePrice = parseFloat(srv.price || 0);
                            if (srv.isPartOfCombo) basePrice = parseFloat(srv.finalPrice || srv.price || 0);
                            if (srv.isCoveredByPackage) basePrice = parseFloat(srv.gettoneMedico || srv.price || 0);
                            
                            // 2. Calcolo Percentuale (cerca nella prestazione, altrimenti usa default 50%)
                            // Nelle vecchie versioni si chiamava doctorPercentage o percentage
                            let pct = parseFloat(srv.doctorPercentage) || parseFloat(srv.percentage) || 50; 
                            
                            totalProd += basePrice;
                            totalComp += (basePrice * (pct / 100));
                        });
                    }
                });

                setStats({ prod: totalProd, comp: totalComp, appts: totalAppts });
            } catch (e) { console.error(e); } finally { setLoading(false); }
        };
        fetchData();
    }, [userProfile]);

    if (loading) return <LoadingWidget />;

    return (
        <div className="dashboard-card">
            <h3 style={{marginBottom: '5px'}}>📊 Rendimento Attuale</h3>
            <p style={{margin: '0 0 15px 0', fontSize: '0.8rem', color: '#64748b', textTransform: 'capitalize'}}>{moment().format('MMMM YYYY')}</p>
            
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px', backgroundColor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0'}}>
                    <div>
                        <div style={{fontSize: '0.85rem', color: '#166534', fontWeight: 'bold'}}>Compenso Maturato</div>
                        <div style={{fontSize: '0.7rem', color: '#15803d', marginTop: '2px'}}>Stima provvisoria (da confermare)</div>
                    </div>
                    <div style={{fontSize: '1.7rem', fontWeight: '900', color: '#16a34a', letterSpacing: '-0.02em'}}>
                        € {stats.comp.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits:2})}
                    </div>
                </div>

                <div style={{display: 'flex', gap: '12px'}}>
                    <div style={{flex: 1, padding: '14px', backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', textAlign: 'center'}}>
                        <div style={{fontSize: '1.3rem', fontWeight: '900', color: '#1d4ed8'}}>{stats.appts}</div>
                        <div style={{fontSize: '0.75rem', color: '#1e40af', fontWeight: '600', marginTop: '2px'}}>Sedute Erogate</div>
                    </div>
                    <div style={{flex: 1, padding: '14px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center'}}>
                        <div style={{fontSize: '1.3rem', fontWeight: '900', color: '#475569'}}>€ {stats.prod.toLocaleString('it-IT', {minimumFractionDigits: 0, maximumFractionDigits:0})}</div>
                        <div style={{fontSize: '0.75rem', color: '#64748b', fontWeight: '600', marginTop: '2px'}}>Prod. Totale</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// REGISTRO DEI WIDGET (CONFIGURAZIONE)
// =============================================================================

export const WIDGET_REGISTRY = {
    // --- WIDGET DEDICATI AI MEDICI ---
    'doctor_compensations': { component: DoctorCompensationsWidget, title: 'I Miei Compensi', defaultSize: '1', checkPermission: (p, user) => user?.isDoctorProfile },
    'doctor_planning': { component: DoctorPlanningWidget, title: 'Il Mio Planning', defaultSize: '1', checkPermission: (p, user) => user?.isDoctorProfile },
    'doctor_stats': { component: DoctorStatsWidget, title: 'Il Mio Rendimento', defaultSize: '1', checkPermission: (p, user) => user?.isDoctorProfile },
    
    // --- GENERALI ---
    'next_shift': { component: NextShiftWidget, title: 'Prossimo Turno', defaultSize: '1', checkPermission: () => true },
    'next_event': { component: NextEventWidget, title: 'Prossimo Evento', defaultSize: '1', checkPermission: () => true },
    'quick_docs': { component: QuickDocsWidget, title: 'Documenti Rapidi', defaultSize: '1', checkPermission: (p) => p?.procedures !== 'none' },
    'personal_notes': { component: PersonalNotesWidget, title: 'Note Personali', defaultSize: '1', checkPermission: () => true },
    'announcements': { component: AnnouncementsWidget, title: 'Bacheca', defaultSize: '1', checkPermission: () => true },
    
    // --- WIDGET LARGHI (2 COLONNE) ---
    'room_planning': { 
        component: RoomPlanningWidget, 
        title: 'Planning Stanze', 
        defaultSize: '2', // <--- IMPORTANTE: 2 significa Full Width
        checkPermission: (p) => p?.rooms !== 'none' 
    },
    'reports': { 
        component: ReportsWidget, 
        title: 'Reportistica', 
        defaultSize: '2', // <--- IMPORTANTE: 2 significa Full Width
        checkPermission: (p) => p?.reports !== 'none' 
    },

    // --- GESTIONALI ---
    'leaves_admin': { component: LeavesAdminWidget, title: 'Gestione Assenze', defaultSize: '1', checkPermission: (p) => p?.leaves === 'completo' || p?.leaves === 'semi-completo' },
    'leaves_self': { component: LeavesSelfWidget, title: 'Le Tue Assenze', defaultSize: '1', checkPermission: (p) => p?.leaves !== 'none' },
    'timeclock_admin': { component: TimeclockAdminWidget, title: 'Presenze Oggi', defaultSize: '1', checkPermission: (p) => p?.staff === 'completo' || p?.staff === 'semi-completo' },
    'timeclock_self': { component: TimeclockSelfWidget, title: 'Timbratrice', defaultSize: '1', checkPermission: (p) => p?.timelogs !== 'none' },
    'payroll': { component: PayrollWidget, title: 'Paghe', defaultSize: '1', checkPermission: (p) => p?.payroll === 'completo' || p?.payroll === 'semi-completo' },
    'payslips': { component: PayslipsWidget, title: 'Buste Paga', defaultSize: '1', checkPermission: (p) => p?.payslips !== 'none' },
    'expense_notes': { component: ExpenseNotesWidget, title: 'Note Spesa', defaultSize: '1', checkPermission: (p) => p?.expenseNotes !== 'none' }
};